import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  FaBed,
  FaBath,
  FaCar,
  FaMapMarkerAlt,
  FaCalendar,
  FaCheckCircle,
} from "react-icons/fa";
import { BiArea } from "react-icons/bi";
import { MdOutlineSecurity } from "react-icons/md";
// import MortgageCalculator from "../components/MortgageCalculator/MortgageCalculator";
import GalleryModal from "../components/Property/GalleryModal";
import "../styles/PropertyDetailsNew.css";
import "leaflet/dist/leaflet.css";
import Meta from "../components/Meta";
import Share from "../components/Share";
import { Alert } from "../components/Alert";
import { HttpClient } from "../utils/HttpClient";
import copyche from "../assets/images/copyche.png";

const PropertyDetails = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    viewing_date_time: "",
    message: "",
  });

  const handleCloseGallery = useCallback(() => {
    setIsGalleryOpen(false);
  }, []);

  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const thumbnailsRef = useRef(null);

  useEffect(() => {
    const fetchProperty = async () => {
      setLoading(true);
      const httpClient = new HttpClient();
      try {
        const response = await httpClient.get(`/properties/${id}`);
        const propertyData = response.data.data;
        setProperty(propertyData);
        setFormData((prevData) => ({
          ...prevData,
          seller_id: propertyData.seller_id,
        }));
      } catch (error) {
        console.error("Error fetching property:", error);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  // Auto-scroll active thumbnail into view
  useEffect(() => {
    if (thumbnailsRef.current && property) {
      const activeThumbnail = thumbnailsRef.current.children[currentImageIndex];
      if (activeThumbnail) {
        activeThumbnail.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [currentImageIndex, property]);

  const handleThumbnailClick = (index) => {
    if (index !== currentImageIndex) {
      setImageLoaded(false);
      setCurrentImageIndex(index);
    }
  };

  const handleNextImage = () => {
    if (property && property.Images.length > 0) {
      setImageLoaded(false);
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % property.Images.length,
      );
    }
  };

  const handlePreviousImage = () => {
    if (property && property.Images.length > 0) {
      setImageLoaded(false);
      setCurrentImageIndex(
        (prevIndex) =>
          (prevIndex - 1 + property.Images.length) % property.Images.length,
      );
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [id]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const httpClient = new HttpClient();
    try {
      await httpClient.post(`/properties/${id}/interestedBuyer`, formData);
      setFormData({
        name: "",
        email: "",
        phone: "",
        viewing_date_time: "",
        message: "",
      });
      setSuccessMessage("Message sent successfully! We'll contact you soon.");

      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    } catch (error) {
      console.error("Error sending message:", error);
      setErrorMessage("Failed to send message. Please try again.");

      // Auto-clear error message after 5 seconds
      setTimeout(() => {
        setErrorMessage("");
      }, 5000);
    }
  };

  if (loading) {
    return (
      <div className="loader">
        <p>Loading property details...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="loader">
        <p>Property not found</p>
      </div>
    );
  }

  const handleCloseAlert = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  return (
    <>
      {property && (
        <>
          <Meta
            title={property.property_name}
            image={property.Images[currentImageIndex]?.url}
            description={property.description}
          />

          {/* HERO SECTION - Large Image Gallery */}
          <div className="property-hero-section">
            <div className="property-hero-main">
              <div className="hero-image-container">
                <img
                  src={property.Images[currentImageIndex]?.url}
                  alt={`${property.property_name} - ${currentImageIndex + 1} of ${property.Images.length}`}
                  className="hero-main-image"
                  onLoad={handleImageLoad}
                  style={{
                    opacity: imageLoaded ? 1 : 0.7,
                    transition: "opacity 400ms ease",
                  }}
                />

                {/* Image Counter and Gallery Button */}
                <div className="hero-image-controls">
                  <div className="image-counter-badge">
                    {currentImageIndex + 1} / {property.Images.length}
                  </div>
                  {property.Images && property.Images.length > 0 && (
                    <button
                      className="gallery-open-btn"
                      onClick={() => setIsGalleryOpen(true)}
                      title={`View all ${property.Images.length} images`}
                      aria-label={`View all ${property.Images.length} images`}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="14" y="14" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                      </svg>
                      <span>View all</span>
                    </button>
                  )}
                </div>

                {/* Navigation Arrows */}
                {property.Images.length > 1 && (
                  <>
                    <button
                      className="hero-nav-arrow hero-prev-arrow"
                      onClick={handlePreviousImage}
                      aria-label="Previous image"
                    >
                      ‹
                    </button>
                    <button
                      className="hero-nav-arrow hero-next-arrow"
                      onClick={handleNextImage}
                      aria-label="Next image"
                    >
                      ›
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail Strip */}
              {property.Images.length > 1 && (
                <div className="hero-thumbnails-container">
                  <div className="hero-thumbnails" ref={thumbnailsRef}>
                    {property.Images.map((image, index) => (
                      <button
                        key={index}
                        className={`hero-thumbnail ${
                          currentImageIndex === index ? "active" : ""
                        }`}
                        onClick={() => handleThumbnailClick(index)}
                        aria-label={`View image ${index + 1} of ${property.Images.length}`}
                        aria-current={
                          currentImageIndex === index ? "true" : "false"
                        }
                      >
                        <img
                          src={image.url}
                          alt={`Thumbnail ${index + 1}`}
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* MAIN CONTENT SECTION */}
          <div className="property-details-main">
            <div className="property-container">
              {/* LEFT COLUMN - Property Details */}
              <div className="property-details-column">
                {/* HEADER WITH PRICE AND TITLE */}
                <div className="property-header-section">
                  <h1 className="property-title">{property.property_name}</h1>
                  <div className="property-header-info">
                    <div className="header-left">
                      <p className="property-address-text">
                        <FaMapMarkerAlt className="icon" />
                        {property.street_name}, {property.house_number},{" "}
                        {property.town}
                      </p>
                      <p className="property-category-badge">
                        {property.category}
                      </p>
                    </div>
                    <div className="header-right">
                      <div className="price-display">
                        <p className="price-label">Price</p>
                        <h2 className="property-price">
                          N$
                          {parseFloat(property.price).toLocaleString("en-US", {
                            maximumFractionDigits: 0,
                          })}
                        </h2>
                      </div>
                    </div>
                  </div>
                </div>

                {/* KEY METRICS - Quick Facts */}
                <div className="property-metrics-section">
                  <div className="metrics-grid">
                    <div className="metric-card">
                      <FaBed className="metric-icon" />
                      <div className="metric-info">
                        <span className="metric-value">
                          {property.bedrooms}
                        </span>
                        <span className="metric-label">Bedrooms</span>
                      </div>
                    </div>
                    <div className="metric-card">
                      <FaBath className="metric-icon" />
                      <div className="metric-info">
                        <span className="metric-value">
                          {property.bathrooms}
                        </span>
                        <span className="metric-label">Bathrooms</span>
                      </div>
                    </div>
                    <div className="metric-card">
                      <FaCar className="metric-icon" />
                      <div className="metric-info">
                        <span className="metric-value">{property.parking}</span>
                        <span className="metric-label">Parking Spaces</span>
                      </div>
                    </div>
                    <div className="metric-card">
                      <BiArea className="metric-icon" />
                      <div className="metric-info">
                        <span className="metric-value">
                          {property.land_size}
                        </span>
                        <span className="metric-label">Land Size m²</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* PROPERTY OVERVIEW */}
                <div className="property-overview-section">
                  <h2 className="section-title">Property Overview</h2>
                  <div className="overview-items">
                    <div className="overview-item">
                      <FaCheckCircle className="overview-icon" />
                      <span>Property Listed</span>
                    </div>
                    <div className="overview-item">
                      <FaCalendar className="overview-icon" />
                      <span>
                        Posted{" "}
                        {new Date(property.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="overview-item">
                      <MdOutlineSecurity className="overview-icon" />
                      <span>Verified Property</span>
                    </div>
                  </div>
                </div>

                {/* DESCRIPTION */}
                <div className="property-description-section">
                  <h2 className="section-title">About This Property</h2>
                  <div className="description-content">
                    {property.description.split("\n").map(
                      (line, index) =>
                        line.trim() && (
                          <p key={index} className="description-paragraph">
                            {line}
                          </p>
                        ),
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN - Sidebar */}
              <div className="property-sidebar">
                {/* AGENT CARD */}
                <div className="agent-card sticky-element">
                  <div className="agent-card-header">
                    <img src={copyche} alt="Agent" className="agent-avatar" />
                  </div>
                  <h3 className="agent-card-title">Contact Agent</h3>
                  <p className="agent-card-subtitle">
                    Get more information about this property
                  </p>

                  {successMessage && (
                    <Alert
                      message={successMessage}
                      type="success"
                      onClose={handleCloseAlert}
                    />
                  )}
                  {errorMessage && (
                    <Alert
                      message={errorMessage}
                      type="error"
                      onClose={handleCloseAlert}
                    />
                  )}

                  <form onSubmit={handleSubmit} className="agent-form">
                    <div className="form-group">
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Full Name"
                        required
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email Address"
                        required
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Phone Number"
                        required
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="datetime-local"
                        id="viewing_date_time"
                        value={formData.viewing_date_time}
                        onChange={handleChange}
                        placeholder="Viewing Date & Time"
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <textarea
                        id="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Additional Message"
                        className="form-input form-textarea"
                        rows="4"
                      />
                    </div>
                    <button type="submit" className="submit-button">
                      Schedule Viewing
                    </button>
                  </form>
                </div>

                {/* MORTGAGE CALCULATOR
                <div className="calculator-card sticky-element">
                  <MortgageCalculator propertyPrice={property.price} />
                </div> */}

                {/* SHARE SECTION */}
                <div className="property-share-section">
                  <Share />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Gallery Modal */}
      {property && property.Images && (
        <GalleryModal
          isOpen={isGalleryOpen}
          images={property.Images.map((img) => img.url)}
          propertyName={property.property_name}
          onClose={handleCloseGallery}
        />
      )}
    </>
  );
};

export default PropertyDetails;
